import React from 'react';
import { Box, Typography } from '@mui/material';
import PageContainer from '../layout/PageContainer';

// Constants for reusable styles and content
const STYLES = {
    container: {
        bgcolor: 'linear-gradient(90deg, #181C1F 0%, #23272A 100%)',
        minHeight: '100vh',
        py: { xs: 3, md: 3 }
    },
    headerSection: {
        textAlign: 'center',
        mb: { xs: 4, md: 6 }
    },
    title: {
        fontSize: { xs: '24px', sm: '2.5rem', md: '2.5rem', lg: '3rem' },
        fontWeight: 700,
        color: '#fff',
        mb: 2,
        fontFamily: "'Wix Madefor Display', sans-serif"
    },
    contentSection: {
        mb: { xs: 3, md: 4 }
    },
    sectionTitle: {
        fontSize: {xs:'12px', sm:'16px', md:'20px'},
        fontWeight: 700,
        fontStyle: 'normal',
        lineHeight: {xs:'20px', md:'28px'},
        letterSpacing: '0%',
        color: '#fff',
        mb: { xs: 2, md: 3 },
        fontFamily: "'Wix Madefor Display', sans-serif"
    },
    paragraph: {
        fontSize: {xs:'12px', sm:'16px', md:'20px'},
        fontWeight: 400,
        fontStyle: 'normal',
        lineHeight: {xs:'20px', md:'28px'},
        letterSpacing: '0%',
        color: 'rgba(200, 200, 200, 1)',
        mb: { xs: 2, md: 3 },
        fontFamily: "'Wix Madefor Display', sans-serif"
    },
    listItem: {
        fontSize: {xs:'12px', sm:'16px', md:'20px'},
        fontWeight: 400,
        fontStyle: 'normal',
        lineHeight: {xs:'20px', md:'28px'},
        letterSpacing: '0%',
        color: 'rgba(200, 200, 200, 1)',
        mb: 1,
        fontFamily: "'Wix Madefor Display', sans-serif",
        pl: 2
    },
    linkText: {
        color: 'rgba(134, 230, 191, 1)',
        textDecoration: 'none',
        fontFamily: "'Wix Madefor Display', sans-serif",
        '&:hover': {
            textDecoration: 'underline'
        }
    }
};

const PRIVACY_CONTENT = {
    introText: "Rubaai Technologies (Pvt) Ltd recognizes that privacy is of utmost importance. We value the privacy of our customers, website visitors, and users of our products and services including the MoiPay Application, POS, and EMS systems. The responsible use and protection of personal and other information under our care is a core value of Rubaai Technologies Pvt Ltd. Please review the following policy to understand how we collect, use, and protect your data.",
    lastUpdated: "Last updated: 04-08-2025",
    sections: [
        {
            title: "1. Introduction",
            content: [
                "1.1 Rubaai Technologies Pvt Ltd is committed to safeguarding the privacy of visitors to www.rubaai.net. This policy outlines how we handle personal data.",
                "1.2 By accessing and using www.rubaai.net, you agree to the terms of this Privacy Policy. We do not currently use cookies, but we reserve the right to introduce them in future updates with user consent."
            ]
        },
        {
            title: "2. Collecting Personal Information",
            content: [
                "2.1 We may collect, store, and use the following types of personal information:"
            ],
            listItems: [
                "Technical information such as IP address, browser type, device information, and geographic location",
                "Information you provide via our website, forms, or when signing up for services (e.g., name, email, phone number)",
                "Information relating to your use of our services, including usage patterns and activity logs",
                "Communication data from emails or forms sent to us",
                "Information shared by third-party partners or integrators (with consent or as legally required)"
            ]
        },
        {
            title: "3. Using Personal Information",
            content: [
                "3.1 We use your personal information to:"
            ],
            listItems: [
                "Operate and manage www.rubaai.net and our business systems",
                "Provide access to our digital services, including the MoiPay App, POS, and EMS systems",
                "Respond to inquiries and provide customer support",
                "Improve our products and user experience",
                "Send non-marketing communications or important service notices",
                "Share anonymized or aggregated user data with third parties for analytics or performance tracking",
                "Comply with legal obligations"
            ]
        },
        {
            title: "3.2 Data Sharing and Security",
            content: [
                "We do not sell or rent your personal information. We may share it with trusted third-party service providers when necessary for operations or compliance, provided they agree to handle it securely."
            ]
        },
        {
            title: "4. Disclosing Personal Information",
            content: [
                "We may disclose your personal data:"
            ],
            listItems: [
                "To service providers, partners, or subcontractors for service delivery",
                "If required by law or regulation",
                "To protect legal rights or manage disputes",
                "In the event of a business acquisition or asset transfer",
                "To third parties only when it aligns with the purposes outlined in this policy"
            ]
        },
        {
            title: "5. International Data Transfers",
            content: [
                "Data may be stored or processed in locations outside Sri Lanka where we or our service providers operate. We ensure that all transfers comply with applicable data protection laws."
            ]
        },
        {
            title: "6. Data Retention",
            content: [
                "6.1 We will retain personal information only as long as necessary for the purposes described above, or as required by law.",
                "6.2 Data submitted through our website or services will generally be deleted after 6 months unless a longer retention period is legally required or for systematic backup or recovery.",
                "",
                "7. Security",
                "We take appropriate technical and organizational measures to protect your data against unauthorized access, loss, misuse, or disclosure.",
                "However, data transmission over the internet can never be guaranteed as 100% secure. You share your information with us at your own risk."
            ]
        },
        {
            title: "8. Your Rights",
            content: [
                "(a) You may request access, correction, or deletion of your personal data",
                "(b) You can opt out of marketing or data collection by contacting us",
                "(c) You may object to processing where legally applicable"
            ]
        },
        {
            title: "9. Third Party Links",
            content: [
                "www.rubaai.net may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. Please review their policies separately."
            ]
        },
        {
            title: "10. Updates to This Policy",
            content: [
                "We may update this Privacy Policy as necessary. The most recent version will always be available on our website. Your continued use of our services constitutes acceptance of any changes."
            ]
        },
        {
            title: "11. Our Details",
            content: [
                "This website is owned and operated by Rubaai Technologies (Pvt) Ltd.",
                "• Our principal place of business is at No.08, Thiruvalluvar Supermarket, Bus Stand Building, Mannar Town, Mannar.",
                "",
                "• You can contact us by writing to the business address given above, by using www.rubaai.net contact form, by email to support@rubaai.net"
            ]
        }
    ]
};

const PrivacyPolicyComponent = () => {
    return (
        <Box sx={STYLES.container}>
            <PageContainer>
                {/* Header Section */}
                <Box sx={STYLES.headerSection}>
                    <Typography sx={STYLES.title}>
                        Privacy Policy
                    </Typography>
                </Box>

                {/* Introduction Text */}
                <Box sx={STYLES.contentSection}>
                    <Typography sx={STYLES.paragraph}>
                        {PRIVACY_CONTENT.introText}
                    </Typography>
                    <Typography sx={{
                        ...STYLES.paragraph,
                        mt: 3,
                        mb: 4
                    }}>
                        {PRIVACY_CONTENT.lastUpdated}
                    </Typography>
                </Box>

                {/* Content Sections */}
                {PRIVACY_CONTENT.sections.map((section, index) => (
                    <Box key={index} sx={STYLES.contentSection}>
                        <Typography sx={STYLES.sectionTitle}>
                            {section.title}
                        </Typography>
                        
                        {section.content && section.content.map((paragraph, pIndex) => (
                            <Typography key={pIndex} sx={STYLES.paragraph}>
                                {paragraph}
                            </Typography>
                        ))}
                        
                        {section.listItems && (
                            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                                {section.listItems.map((item, lIndex) => (
                                    <Typography 
                                        key={lIndex} 
                                        component="li" 
                                        sx={STYLES.listItem}
                                    >
                                        {item}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                ))}
            </PageContainer>
        </Box>
    );
};

export default PrivacyPolicyComponent;